import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", manufacturer: "", code: "", supplier: "", cost: 0, suggested_price: 0, stock: 0, notes: "" };

export default function Parts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/parts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: ["cost", "suggested_price", "stock"].includes(k) ? Number(e.target.value) || 0 : e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/parts/${editing}`, form);
      else await api.post("/parts", form);
      toast.success(editing ? "Peça atualizada!" : "Peça cadastrada!");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta peça?")) return;
    await api.delete(`/parts/${id}`);
    load();
  };

  return (
    <div className="space-y-5 animate-in-up" data-testid="parts-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Banco de peças</h1>
          <p className="text-sm text-slate-500">{items.length} peças cadastradas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={openNew} data-testid="new-part-btn">
              <Plus className="h-4 w-4 mr-2" /> Nova peça
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar peça" : "Nova peça"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5"><Label>Nome *</Label><Input className="h-12" data-testid="part-name" required value={form.name} onChange={set("name")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Fabricante</Label><Input className="h-12" data-testid="part-manufacturer" value={form.manufacturer} onChange={set("manufacturer")} /></div>
                <div className="space-y-1.5"><Label>Código</Label><Input className="h-12" data-testid="part-code" value={form.code} onChange={set("code")} /></div>
                <div className="space-y-1.5"><Label>Fornecedor</Label><Input className="h-12" data-testid="part-supplier" value={form.supplier} onChange={set("supplier")} /></div>
                <div className="space-y-1.5"><Label>Estoque</Label><Input className="h-12 font-mono-num" type="number" data-testid="part-stock" value={form.stock} onChange={set("stock")} /></div>
                <div className="space-y-1.5"><Label>Custo (R$)</Label><Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="part-cost" value={form.cost} onChange={set("cost")} /></div>
                <div className="space-y-1.5"><Label>Preço sugerido (R$)</Label><Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="part-price" value={form.suggested_price} onChange={set("suggested_price")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Observação</Label><Input className="h-12" data-testid="part-notes" value={form.notes} onChange={set("notes")} /></div>
              <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="part-save-btn">Salvar peça</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {items.length === 0 && (
          <Card className="border-slate-200 sm:col-span-2"><CardContent className="p-8 text-center text-slate-500 text-sm">
            Nenhuma peça cadastrada. Peças salvas podem ser reutilizadas nos orçamentos.
          </CardContent></Card>
        )}
        {items.map((p) => (
          <Card key={p.id} className="border-slate-200 shadow-sm" data-testid={`part-card-${p.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-slate-500">{[p.manufacturer, p.code, p.supplier].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => openEdit(p)} data-testid={`part-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => remove(p.id)} data-testid={`part-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="font-mono-num">Custo: <b>{brl(p.cost)}</b></span>
                <span className="font-mono-num text-green-700">Venda: <b>{brl(p.suggested_price)}</b></span>
                {p.stock > 0 && <span className="text-slate-500">Est.: {p.stock}</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
